// src/contexts/AnalyticsContext.tsx
import React, {
    createContext,
    useContext,
    ReactNode,
} from 'react';
import { useEvents } from './EventsContext';
import { useAuth } from './AuthContext';
import { useUsers } from './UsersContext';
import { format } from 'date-fns';

const AnalyticsContext = createContext<any | null>(null);

export const useAnalytics = () => {
    const context = useContext(AnalyticsContext);
    if (!context) {
        throw new Error(
            'useAnalytics must be used within an AnalyticsProvider',
        );
    }
    return context;
};

export const AnalyticsProvider: React.FC<{ children: ReactNode }> = ({
    children,
}) => {
    const {
        bookings,
        getBookingPrice,
        busySlots,

        getPerformanceById,
    } = useEvents();
    const { users, getUserById } = useUsers();
    const { organization } = useAuth();

    const getValidBookings = (b: any[]) => {
        if (!b || !Array.isArray(b)) return [];
        return b.filter(
            (booking) =>
                booking.status !== 'cancelled' && booking.status !== 'no_show',
        );
    };

    const getBookingsByValidHairdressers = (b: any[]) => {
        if (!b || !Array.isArray(b)) return [];
        return b.filter((booking) => {
            const hairdresser = getUserById(booking.hairdresser_id);
            if (
                !hairdresser ||
                hairdresser.is_active !== true
            ) {
                return false;
            }
            return true;
        });
    };

    const filterDataByPeriod = (
        data: any[],
        period:
            | 'ever'
            | 'current_week'
            | 'year'
            | 'month'
            | 'week'
            | 'day',
        dataType: 'clients' | 'bookings' = 'clients',
    ) => {
        if (!data || !Array.isArray(data)) return [];

        if (period === 'ever') return data;

        const now = new Date();
        now.setDate(now.getDate() + 1);
        now.setHours(0, 0, 0, 0);

        let startDate: Date;

        if (period === 'current_week') {
            const today = new Date();
            const dayOfWeek = today.getDay(); // 0 = Sun, 1 = Mon...
            const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
            startDate = new Date(today);
            startDate.setDate(today.getDate() - daysFromMonday);
            startDate.setHours(0, 0, 0, 0);

            const endDate = new Date(startDate);
            endDate.setDate(startDate.getDate() + 7);
            endDate.setHours(0, 0, 0, 0);

            return data.filter((item) => {
                const dateField =
                    dataType === 'bookings' ? item.date : item.created_at;
                const itemDate = new Date(dateField);
                return itemDate >= startDate && itemDate < endDate;
            });
        } else {
            startDate =
                period === 'year'
                    ? new Date(
                        now.getFullYear() - 1,
                        now.getMonth(),
                        now.getDate(),
                    )
                    : period === 'month'
                        ? new Date(
                            now.getFullYear(),
                            now.getMonth() - 1,
                            now.getDate(),
                        )
                        : period === 'week'
                            ? new Date(
                                now.getFullYear(),
                                now.getMonth(),
                                now.getDate() - 7,
                            )
                            : new Date(
                                now.getFullYear(),
                                now.getMonth(),
                                now.getDate() - 1,
                            );
        }

        return data.filter((item) => {
            const dateField =
                dataType === 'bookings' ? item.date : item.created_at;
            const itemDate = new Date(dateField);
            return itemDate >= startDate && itemDate < now;
        });
    };

    const getPreviousPeriodData = (
        data: any[],
        period: 'ever' | 'year' | 'month' | 'week' | 'day',
        dataType: 'clients' | 'bookings' = 'clients',
    ) => {
        if (!data || !Array.isArray(data)) return [];
        if (period === 'ever') return [];

        const now = new Date();
        now.setDate(now.getDate() + 1);
        now.setHours(0, 0, 0, 0);
        let startDate: Date;
        let endDate: Date;

        if (period === 'day') {
            startDate = new Date(
                now.getFullYear(),
                now.getMonth(),
                now.getDate() - 2,
            );
            endDate = new Date(
                now.getFullYear(),
                now.getMonth(),
                now.getDate() - 1,
            );
        } else if (period === 'week') {
            startDate = new Date(
                now.getFullYear(),
                now.getMonth(),
                now.getDate() - 14,
            );
            endDate = new Date(
                now.getFullYear(),
                now.getMonth(),
                now.getDate() - 7,
            );
        } else if (period === 'month') {
            startDate = new Date(
                now.getFullYear(),
                now.getMonth() - 2,
                now.getDate(),
            );
            endDate = new Date(
                now.getFullYear(),
                now.getMonth() - 1,
                now.getDate(),
            );
        } else { // year
            startDate = new Date(
                now.getFullYear() - 2,
                now.getMonth(),
                now.getDate(),
            );
            endDate = new Date(
                now.getFullYear() - 1,
                now.getMonth(),
                now.getDate(),
            );
        }

        return data.filter((item) => {
            const dateField =
                dataType === 'bookings' ? item.date : item.created_at;
            const itemDate = new Date(dateField);
            return itemDate >= startDate && itemDate < endDate;
        });
    };

    const calculateLoyaltyRate = (
        currentBookings: any[],
        timeRange: string,
    ) => {
        let loyaltyRate = 0;
        if (!['day', 'week'].includes(timeRange)) {
            const clientBookingCounts: Record<string, number> = {};
            currentBookings.forEach((booking) => {
                if (booking.client_id) {
                    clientBookingCounts[booking.client_id] =
                        (clientBookingCounts[booking.client_id] || 0) + 1;
                }
            });

            const clientsWithMultipleBookings = Object.values(
                clientBookingCounts,
            ).filter((count) => count > 1).length;
            const clientsCount = Object.values(clientBookingCounts).length;

            loyaltyRate =
                clientsCount > 0
                    ? (clientsWithMultipleBookings / clientsCount) * 100
                    : 0;
        }
        return loyaltyRate;
    };

    const calculateHourlyDistribution = (
        b: any[],
        timeRange: string,
        dataType: 'bookings' | 'revenue',
    ) => {
        const timeToMinutes = (time: string | null) => {
            if (!time) return null;
            const [hours, minutes] = time.split(':').map(Number);
            return hours * 60 + minutes;
        };

        const weekdays = [
            'sunday',
            'monday',
            'tuesday',
            'wednesday',
            'thursday',
            'friday',
            'saturday',
        ];

        let earliestOpen: number | null = null;
        let latestClose: number | null = null;

        if (organization?.time_ranges) {
            weekdays.forEach((day) => {
                const dayHours = organization.time_ranges[day];
                if (dayHours?.open && dayHours?.close) {
                    const openMinutes = timeToMinutes(dayHours.open)!;
                    const closeMinutes = timeToMinutes(dayHours.close)!;

                    if (earliestOpen === null || openMinutes < earliestOpen) {
                        earliestOpen = openMinutes;
                    }
                    if (latestClose === null || closeMinutes > latestClose) {
                        latestClose = closeMinutes;
                    }
                }
            });
        }

        if (earliestOpen === null) earliestOpen = 8 * 60;
        if (latestClose === null) latestClose = 20 * 60;

        const startHour = Math.floor(earliestOpen / 60);
        const endHour = Math.ceil(latestClose / 60);

        const hourIntervals: any[] = [];
        for (let hour = startHour; hour < endHour; hour++) {
            const startMinutes = hour * 60;
            const endMinutes = startMinutes + 45;
            hourIntervals.push({
                hour: `${hour.toString().padStart(2, '0')}:00`,
                value: 0,
                startMinutes,
                endMinutes,
            });
        }

        let filteredBookings = filterDataByPeriod(
            b,
            timeRange as any,
            'bookings',
        );

        filteredBookings.forEach((booking: any) => {
            const [hours, minutes] = booking.time.split(':').map(Number);
            const bookingMinutes = hours * 60 + minutes;

            const hourInterval = hourIntervals.find(
                (interval) =>
                    bookingMinutes >= interval.startMinutes &&
                    bookingMinutes <= interval.endMinutes,
            );

            if (hourInterval) {
                if (dataType === 'bookings') {
                    hourInterval.value++;
                } else {
                    hourInterval.value += getBookingPrice(booking);
                }
            }
        });

        return hourIntervals.map((h) => ({ name: h.hour, value: h.value }));
    };

    const calculateWeeklyDistribution = (
        b: any[],
        timeRange: string,
        dataType: 'bookings' | 'revenue',
    ) => {
        if (timeRange === 'day') return [];

        const allDays = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];
        let days: { name: string; dayIndex: number }[] = [];

        if (timeRange === 'current_week') {
            const daysOrder = [1, 2, 3, 4, 5, 6, 0];
            days = daysOrder.map((dayIndex) => ({
                name: allDays[dayIndex],
                dayIndex,
            }));
        } else {
            const today = new Date().getDay();
            for (let i = 6; i >= 0; i--) {
                const dayIndex = (today - i + 7) % 7;
                days.push({ name: allDays[dayIndex], dayIndex });
            }
        }

        const weekData = days.map((day) => ({
            name: day.name,
            value: 0,
            dayIndex: day.dayIndex,
        }));

        let filteredBookings = filterDataByPeriod(
            b,
            timeRange as any,
            'bookings',
        );

        filteredBookings.forEach((booking: any) => {
            const bookingDate = new Date(booking.date);
            const dayOfWeek = bookingDate.getDay();

            const weekDataIndex = weekData.findIndex(
                (day) => day.dayIndex === dayOfWeek,
            );
            if (weekDataIndex !== -1) {
                if (dataType === 'bookings') {
                    weekData[weekDataIndex].value++;
                } else {
                    weekData[weekDataIndex].value += getBookingPrice(booking);
                }
            }
        });

        return weekData;
    };

    const calculateMonthlyDistribution = (
        b: any[],
        timeRange: string,
        dataType: 'bookings' | 'revenue',
    ) => {
        if (['day', 'week', 'month'].includes(timeRange)) return [];

        const months = [
            'Gen',
            'Feb',
            'Mar',
            'Apr',
            'Mag',
            'Giu',
            'Lug',
            'Ago',
            'Set',
            'Ott',
            'Nov',
            'Dic',
        ];
        const monthData = months.map((month, index) => ({
            name: month,
            value: 0,
            monthIndex: index,
        }));

        let filteredBookings: any[];

        if (timeRange === 'year') {
            const currentYear = new Date().getFullYear();
            filteredBookings = b.filter((booking: any) => {
                const bookingDate = new Date(booking.date);
                return bookingDate.getFullYear() === currentYear;
            });
        } else {
            filteredBookings = b;
        }

        filteredBookings.forEach((booking: any) => {
            const bookingDate = new Date(booking.date);
            const month = bookingDate.getMonth();
            if (dataType === 'bookings') {
                monthData[month].value++;
            } else {
                monthData[month].value += getBookingPrice(booking);
            }
        });

        return monthData;
        return monthData;
    };

    const calculateDailyDistribution = (
        b: any[],
        timeRange: string,
        dataType: 'bookings' | 'revenue',
    ) => {
        if (timeRange !== 'month') return [];

        const today = new Date();
        const data: { name: string; value: number; dateStr: string }[] = [];

        // Generate last 30 days
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - 29); // 30 days including today
        startDate.setHours(0, 0, 0, 0);

        for (let i = 0; i < 30; i++) {
            const d = new Date(startDate);
            d.setDate(startDate.getDate() + i);
            const dateStr = format(d, 'yyyy-MM-dd');
            const label = format(d, 'dd/MM');
            data.push({ name: label, value: 0, dateStr });
        }

        b.forEach(booking => {
            const dateStr = booking.date; // yyyy-MM-dd
            const dayData = data.find(d => d.dateStr === dateStr);
            if (dayData) {
                if (dataType === 'bookings') {
                    dayData.value++;
                } else {
                    dayData.value += getBookingPrice(booking);
                }
            }
        });

        return data.map(({ name, value }) => ({ name, value }));
    };

    const calculateOccupancyRate = (currentBookings: any[]) => {
        if (!users || !busySlots || !organization?.time_ranges) return 0;

        const timeToMinutes = (time: string | null) => {
            if (!time) return null;
            const [hours, minutes] = time.split(':').map(Number);
            return hours * 60 + minutes;
        };

        const today = new Date();
        const weekdays = [
            'sunday',
            'monday',
            'tuesday',
            'wednesday',
            'thursday',
            'friday',
            'saturday',
        ];

        const currentDay = today.getDay();
        const daysToMonday = currentDay === 0 ? 6 : currentDay - 1;
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - daysToMonday);

        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);

        let totalOccupiedMinutes = 0;
        let totalAvailableMinutes = 0;

        for (let dayOffset = 0; dayOffset < 7; dayOffset++) {
            const currentDate = new Date(weekStart);
            currentDate.setDate(weekStart.getDate() + dayOffset);
            const dateString = format(currentDate, 'yyyy-MM-dd');
            const dayOfWeek = weekdays[currentDate.getDay()];

            const shopHoliday = busySlots.find(
                (slot: any) =>
                    slot.hairdresser_id === null &&
                    slot.type === 'holiday' &&
                    dateString >= slot.start_date &&
                    dateString <= slot.end_date,
            );

            if (shopHoliday) continue;

            const shopHours = organization.time_ranges?.[dayOfWeek];
            if (!shopHours?.open) continue;

            const dayBookings = currentBookings.filter(
                (booking: any) => booking.date === dateString,
            );

            const dayOccupiedMinutes = dayBookings.reduce(
                (total: number, booking: any) => {
                    const performance = getPerformanceById(
                        booking.performance_id,
                    );
                    const duration = performance?.duration || 60;
                    return total + duration;
                },
                0,
            );

            totalOccupiedMinutes += dayOccupiedMinutes;

            let dayAvailableMinutes = 0;

            users.forEach((employee: any) => {
                const employeeHoliday = busySlots.find(
                    (slot: any) =>
                        slot.hairdresser_id === employee.id &&
                        slot.type === 'holiday' &&
                        dateString >= slot.start_date &&
                        dateString <= slot.end_date,
                );

                if (employeeHoliday) return;

                const employeeHours = employee.time_ranges?.[dayOfWeek];
                if (!employeeHours?.open) return;

                const startMinutes = Math.max(
                    timeToMinutes(employeeHours.open)!,
                    timeToMinutes(shopHours.open)!,
                );
                const endMinutes = Math.min(
                    timeToMinutes(employeeHours.close)!,
                    timeToMinutes(shopHours.close)!,
                );

                if (startMinutes >= endMinutes) return;

                let employeeDayMinutes = endMinutes - startMinutes;

                if (employeeHours.start_break && employeeHours.end_break) {
                    const breakStart = timeToMinutes(employeeHours.start_break)!;
                    const breakEnd = timeToMinutes(employeeHours.end_break)!;

                    const effectiveBreakStart = Math.max(
                        breakStart,
                        startMinutes,
                    );
                    const effectiveBreakEnd = Math.min(breakEnd, endMinutes);

                    if (effectiveBreakStart < effectiveBreakEnd) {
                        employeeDayMinutes -=
                            effectiveBreakEnd - effectiveBreakStart;
                    }
                }

                const dayPermits = busySlots.filter(
                    (slot: any) =>
                        slot.hairdresser_id === employee.id &&
                        slot.type === 'permit' &&
                        dateString >= slot.start_date &&
                        dateString <= slot.end_date,
                );

                dayPermits.forEach((permit: any) => {
                    const permitStart = timeToMinutes(permit.start_time)!;
                    const permitEnd = timeToMinutes(permit.end_time)!;

                    const effectivePermitStart = Math.max(
                        permitStart,
                        startMinutes,
                    );
                    const effectivePermitEnd = Math.min(permitEnd, endMinutes);

                    if (effectivePermitStart < effectivePermitEnd) {
                        employeeDayMinutes -=
                            effectivePermitEnd - effectivePermitStart;
                    }
                });

                dayAvailableMinutes += Math.max(0, employeeDayMinutes);
            });

            totalAvailableMinutes += dayAvailableMinutes;
        }

        if (totalAvailableMinutes === 0) return 0;

        const occupancyRate = Math.min(
            (totalOccupiedMinutes / totalAvailableMinutes) * 100,
            100,
        );

        return occupancyRate;
    };

    const dailyOccupancyRate = () => {
        if (!users || !busySlots || !organization?.time_ranges) return 0;

        const timeToMinutes = (time: string | null) => {
            if (!time) return null;
            const [hours, minutes] = time.split(':').map(Number);
            return hours * 60 + minutes;
        };

        const today = new Date();
        const weekdays = [
            'sunday',
            'monday',
            'tuesday',
            'wednesday',
            'thursday',
            'friday',
            'saturday',
        ];
        const dayOfWeek = weekdays[today.getDay()];
        const dateString = format(today, 'yyyy-MM-dd');

        const shopHoliday = busySlots.find(
            (slot: any) =>
                slot.hairdresser_id === null &&
                slot.type === 'holiday' &&
                dateString >= slot.start_date &&
                dateString <= slot.end_date,
        );

        if (shopHoliday) return 0;

        const shopHours = organization.time_ranges?.[dayOfWeek];
        if (!shopHours?.open) return 0;

        const todayBookings = bookings.filter(
            (booking: any) =>
                booking.date === dateString &&
                booking.status !== 'cancelled' &&
                booking.status !== 'no_show'
        );

        const dailyOccupiedMinutes = todayBookings.reduce(
            (total: number, booking: any) => {
                const performance = getPerformanceById(booking.performance_id);
                const duration = performance?.duration || 60;
                return total + duration;
            },
            0,
        );

        let dailyAvailableMinutes = 0;

        users.forEach((employee: any) => {
            const employeeHoliday = busySlots.find(
                (slot: any) =>
                    slot.hairdresser_id === employee.id &&
                    slot.type === 'holiday' &&
                    dateString >= slot.start_date &&
                    dateString <= slot.end_date,
            );

            if (employeeHoliday) return;

            const employeeHours = employee.time_ranges?.[dayOfWeek];
            if (!employeeHours?.open) return;

            const startMinutes = Math.max(
                timeToMinutes(employeeHours.open)!,
                timeToMinutes(shopHours.open)!,
            );
            const endMinutes = Math.min(
                timeToMinutes(employeeHours.close)!,
                timeToMinutes(shopHours.close)!,
            );

            if (startMinutes >= endMinutes) return;

            let employeeDailyMinutes = endMinutes - startMinutes;

            if (employeeHours.start_break && employeeHours.end_break) {
                const breakStart = timeToMinutes(employeeHours.start_break)!;
                const breakEnd = timeToMinutes(employeeHours.end_break)!;

                const effectiveBreakStart = Math.max(
                    breakStart,
                    startMinutes,
                );
                const effectiveBreakEnd = Math.min(breakEnd, endMinutes);

                if (effectiveBreakStart < effectiveBreakEnd) {
                    employeeDailyMinutes -=
                        effectiveBreakEnd - effectiveBreakStart;
                }
            }

            const todayPermits = busySlots.filter(
                (slot: any) =>
                    slot.hairdresser_id === employee.id &&
                    slot.type === 'permit' &&
                    dateString >= slot.start_date &&
                    dateString <= slot.end_date,
            );

            todayPermits.forEach((permit: any) => {
                const permitStart = timeToMinutes(permit.start_time)!;
                const permitEnd = timeToMinutes(permit.end_time)!;

                const effectivePermitStart = Math.max(
                    permitStart,
                    startMinutes,
                );
                const effectivePermitEnd = Math.min(permitEnd, endMinutes);

                if (effectivePermitStart < effectivePermitEnd) {
                    employeeDailyMinutes -=
                        effectivePermitEnd - effectivePermitStart;
                }
            });

            dailyAvailableMinutes += Math.max(0, employeeDailyMinutes);
        });

        if (dailyAvailableMinutes === 0) return 0;

        const occupancyRate = Math.min(
            (dailyOccupiedMinutes / dailyAvailableMinutes) * 100,
            100,
        );

        return occupancyRate;
    };

    const calculateRevenue = (b: any[]) =>
        b.reduce((total, booking) => {
            const performance = getPerformanceById(booking.performance_id);
            const basePrice = performance?.price || 0;
            const adjustment = Number(booking.price_adjustment) || 0;
            return total + basePrice + adjustment;
        }, 0);

    const buildTopServices = (validBookings: any[] = []) => {
        const counts = new Map<string, number>();
        for (const b of validBookings) {
            const perfName = getPerformanceById(b?.performance_id)?.name;
            if (!perfName) continue;
            counts.set(perfName, (counts.get(perfName) || 0) + 1);
        }

        if (counts.size === 0) return [];

        let result = Array.from(counts.entries())
            .map(([name, value]) => ({
                name,
                value,
            }))
            .sort((a, b) => b.value - a.value)
            .slice(0, 3);

        if (result.length < 3) {
            result.push({ name: 'Altro', value: 0 });
        } else {
            result[2].name = 'Altro';
        }

        return result;
    };

    const value = {
        filterDataByPeriod,
        getPreviousPeriodData,
        calculateLoyaltyRate,
        calculateHourlyDistribution,
        calculateWeeklyDistribution,
        calculateMonthlyDistribution,
        calculateDailyDistribution,
        calculateRevenue,
        calculateOccupancyRate,
        dailyOccupancyRate,
        getValidBookings,
        getBookingsByValidHairdressers,
        buildTopServices,
    };

    return (
        <AnalyticsContext.Provider value={value}>
            {children}
        </AnalyticsContext.Provider>
    );
};